<template>
  <BaseModal :modalTitle="$t('W: Opret lokation')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="name"
        :label="$t('term.name')"
        :rules="['required']"
        isRequired
        showAsRequired
        v-model="newLabLocation.name"
        @update:model-value="resetValidationMessages" />

      <UiInputTextFieldVee
        name="address"
        :label="$t('form.label.address')"
        v-model="newLabLocation.address"
        @update:model-value="resetValidationMessages">
        <template v-if="isMagicAddressEnabled" #trailingIcon>
          <MagicAddressButton
            :currentAddressLine="newLabLocation.address"
            @on-fill="onMagicAddress" />
        </template>
      </UiInputTextFieldVee>
      <div class="grid grid-cols-2 gap-4">
        <UiInputTextFieldVee
          name="postalCode"
          :label="$t('form.label.postal-code')"
          v-model="newLabLocation.postalCode"
          @update:model-value="resetValidationMessages" />
        <UiInputTextFieldVee
          name="city"
          :label="$t('form.label.city')"
          v-model="newLabLocation.city"
          @update:model-value="resetValidationMessages" />
      </div>

      <UiComboboxVee
        name="responsibleEmployeeID"
        :label="$t('W: Ansvarlig medarbejder')"
        :placeholder="$t('W: Ingen valgt')"
        :options="responsibleEmployeeOptions"
        v-model="newLabLocation.responsibleEmployeeID"
        @update:model-value="resetValidationMessages" />

      <div class="mb-6 flex items-center gap-2">
        <AppSwitch
          :isActive="newLabLocation.canForwardSamples"
          :title="$t('W: Kan videresende prøver')"
          @on-toggle="
            newLabLocation.canForwardSamples = !newLabLocation.canForwardSamples;
            resetValidationMessages()
          " />
        <span class="text-sm text-text-primary">
          {{ $t('W: Kan videresende prøver') }}
        </span>
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { canUseMagicAddress } from '@/dev/magicAddress'
import type { IMagicAddress } from '@/dev/magicAddress'
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { useToast } from '@/globals/typescript/utils/toast'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { mapActions, mapState } from 'pinia'

type TCreateLabLocationForm = {
  name: string
  address: string
  postalCode: string
  city: string
  responsibleEmployeeID: string
  canForwardSamples: boolean
}

function createLabLocationForm(): TCreateLabLocationForm {
  return {
    name: '',
    address: '',
    postalCode: '',
    city: '',
    responsibleEmployeeID: '',
    canForwardSamples: false,
  }
}

export default {
  name: 'CreateLabLocationModal',

  emits: ['close'],

  data() {
    return {
      isMagicAddressEnabled: canUseMagicAddress(),
      unsavedGuard: useUnsavedChangesGuard(),
      newLabLocation: createLabLocationForm(),

      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAccountStore, ['labAccount', 'allLabAccountUsers']),
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),

    responsibleEmployeeOptions(): { value: string; text: string }[] {
      const activeUsers = this.allLabAccountUsers.filter(
        (user) => user.activeState === 'active'
      )
      return [
        { value: '', text: this.$t('W: Ingen valgt') },
        ...activeUsers
          .map((user) => ({ value: user.id, text: user.fullName }))
          .sort((a, b) => a.text.localeCompare(b.text)),
      ]
    },
  },

  methods: {
    onMagicAddress(address: IMagicAddress) {
      this.newLabLocation.address = address.addressLine
      this.newLabLocation.postalCode = address.postalCode
      this.newLabLocation.city = address.city
      this.resetValidationMessages()
    },

    ...mapActions(useLabLocationStore, ['createLabLocation']),

    async onSubmit() {
      this.isLoading = true
      this.resetValidationMessages()

      try {
        if (!this.labAccount?.id) {
          this.errorText = this.$t('error.common.standard')
          return
        }

        const result = await this.createLabLocation({
          labAccountID: this.labAccount.id,
          data: {
            name: this.newLabLocation.name,
            address: {
              address: this.newLabLocation.address,
              postalCode: this.newLabLocation.postalCode,
              city: this.newLabLocation.city,
            },
            responsibleEmployeeID: this.newLabLocation.responsibleEmployeeID || null,
            canForwardSamples: this.newLabLocation.canForwardSamples,
          },
        })

        if (result.success) {
          this.showSaveIcon = true
          setTimeout(() => {
            this.showSaveIcon = false
          }, this.defaultMessageSaveIconTime)
          this.unsavedGuard.takeSnapshot()
          useToast().success(this.$t('W: Lokation oprettet'))
          this.$emit('close')
          return
        }

        useToast().error(this.$t('W: Kunne ikke oprette lokation'))
        this.errorText = getErrorMessage(result.code)
      } finally {
        this.isLoading = false
      }
    },

    formResetCallback(): boolean {
      return !this.errorText
    },

    resetValidationMessages() {
      this.errorText = ''
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.newLabLocation }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
